import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { AdminColumn, AdminDetailDrawer, AdminTable, Field, Section, useAdminFetch } from "../../components/admin";
import { Alert, AlertDescription, Button, Dialog, DropdownField, Icon, InputField, Text, View } from "../../components/base";
import { PageLayout } from "../../components/PageLayout";
import { formatDateShort } from "../../utils/adminFormat";
import { makeRequest } from "../../utils/request";

// ---------------------------------------------------------------------------
// Types — mirror the /v1/admin/*-model-definitions responses.
// ---------------------------------------------------------------------------

type BaseRow = {
  id: string;
  modelName: string;
  source: string;
  validFrom: string;
  validTo: string | null;
  createdAt: string;
  updatedAt: string;
};

type CompletionsRow = BaseRow & {
  apiProvider: string;
  inputCostPerMillionTokens: number;
  outputCostPerMillionTokens: number;
  cacheReadCostPerMillionTokens: number | null;
  cacheWriteCostPerMillionTokens: number | null;
  enabledEvalModalities: string[] | null;
  notes: string | null;
};

type TtsRow = BaseRow & {
  provider: string;
  inputCostPerMillionCharacters: number | null;
  inputCostPerMillionUtf8Bytes: number | null;
  outputCostPerMillionAudioTokens: number | null;
  inputCostPerMillionTextTokens: number | null;
  notes: string | null;
};

type SttRow = BaseRow & {
  provider: string;
  inputCostPerMinuteLiveAudio: number | null;
  inputCostPerMinuteUploadedFile: number | null;
  inputCostPerMillionAudioTokens: number | null;
  outputCostPerMillionTextTokens: number | null;
  notes: string | null;
};

type RealtimeRow = BaseRow & {
  provider: string;
  inputCostPerMillionAudioTokens: number | null;
  outputCostPerMillionAudioTokens: number | null;
  inputCostPerMillionTextTokens: number | null;
  outputCostPerMillionTextTokens: number | null;
  cacheReadCostPerMillionAudioTokens: number | null;
  cacheReadCostPerMillionTextTokens: number | null;
  notes: string | null;
};

// One kind per rate table — TTS and STT are separate tables server-side
// (they share no rate columns), so the page needs no mode plumbing.
type Kind = "completions" | "tts" | "stt" | "realtime";

// No /admin prefix: these routes are slated to grow a non-admin read
// surface; auth is platform-scoped (model-pricing:*) for now.
const ENDPOINTS: Record<Kind, string> = {
  completions: "/v1/completions-model-definitions",
  tts: "/v1/tts-model-definitions",
  stt: "/v1/stt-model-definitions",
  realtime: "/v1/realtime-model-definitions",
};

// ---------------------------------------------------------------------------
// Rate field specs — drive both the table rate cells and the dialog inputs.
// ---------------------------------------------------------------------------

type RateField = { key: string; label: string; short: string; hint?: string };

const COMPLETIONS_RATE_FIELDS: RateField[] = [
  { key: "inputCostPerMillionTokens", label: "Input ($ / M tokens)", short: "in" },
  { key: "outputCostPerMillionTokens", label: "Output ($ / M tokens)", short: "out" },
  { key: "cacheReadCostPerMillionTokens", label: "Cache read ($ / M tokens)", short: "cache read" },
  { key: "cacheWriteCostPerMillionTokens", label: "Cache write ($ / M tokens)", short: "cache write" },
];

const TTS_RATE_FIELDS: RateField[] = [
  { key: "inputCostPerMillionCharacters", label: "$ / M characters", short: "M chars" },
  { key: "inputCostPerMillionUtf8Bytes", label: "$ / M UTF-8 bytes", short: "M bytes" },
  { key: "outputCostPerMillionAudioTokens", label: "$ / M audio output tokens", short: "M audio tok" },
  { key: "inputCostPerMillionTextTokens", label: "$ / M text input tokens", short: "M text tok" },
];

const STT_RATE_FIELDS: RateField[] = [
  // Per-second rates split by how the audio ARRIVES. Null means the modality
  // doesn't exist / isn't priced for the model (live-only models like
  // Cartesia ink, file-only models like whisper-1) — never mirror one
  // modality's price into the other.
  {
    key: "inputCostPerMinuteLiveAudio",
    label: "$ / minute (live audio)",
    short: "min live",
    hint: "Websocket STT — what voice sessions bill.",
  },
  {
    key: "inputCostPerMinuteUploadedFile",
    label: "$ / minute (uploaded file)",
    short: "min file",
    hint: "Sync or batch file transcription — not billed yet.",
  },
  { key: "inputCostPerMillionAudioTokens", label: "$ / M audio input tokens", short: "M audio tok" },
  {
    key: "outputCostPerMillionTextTokens",
    label: "$ / M text output tokens",
    short: "M text tok",
    hint: "The transcript meter — engine capture pending, prices $0 until then.",
  },
];

const REALTIME_RATE_FIELDS: RateField[] = [
  { key: "inputCostPerMillionAudioTokens", label: "$ / M audio input tokens", short: "M audio in" },
  { key: "outputCostPerMillionAudioTokens", label: "$ / M audio output tokens", short: "M audio out" },
  { key: "inputCostPerMillionTextTokens", label: "$ / M text input tokens", short: "M text in" },
  { key: "outputCostPerMillionTextTokens", label: "$ / M text output tokens", short: "M text out" },
  {
    key: "cacheReadCostPerMillionAudioTokens",
    label: "$ / M cached audio input tokens",
    short: "M cache aud",
    hint: "Optional — cached input bills at the full input rate when unset.",
  },
  {
    key: "cacheReadCostPerMillionTextTokens",
    label: "$ / M cached text input tokens",
    short: "M cache txt",
    hint: "Optional — cached input bills at the full input rate when unset.",
  },
];

const RATE_FIELDS: Record<Kind, RateField[]> = {
  completions: COMPLETIONS_RATE_FIELDS,
  tts: TTS_RATE_FIELDS,
  stt: STT_RATE_FIELDS,
  realtime: REALTIME_RATE_FIELDS,
};

// ---------------------------------------------------------------------------
// Pricing strategies — a speech rate row bills in exactly ONE (enforced by
// the API and DB CHECK constraints). The dialog exposes a strategy picker
// and only the picked strategy's fields.
// ---------------------------------------------------------------------------

type StrategyDef = {
  id: string;
  /** Dropdown label (carries the units). */
  label: string;
  /** Compact display name for the drawer. */
  name: string;
  fieldKeys: string[];
  /** Rates that belong to the strategy but may be left blank (realtime's
   *  cache-read discounts). Shown in the dialog, never required. */
  optionalFieldKeys?: string[];
  /** True: every fieldKeys entry required (TTS token bills both meters per
   *  request). False: at least one (STT duration's live/file are separate
   *  modalities a provider may or may not offer). */
  requireAll: boolean;
};

const PRICING_STRATEGIES: Partial<Record<Kind, StrategyDef[]>> = {
  tts: [
    { id: "character", label: "Character ($ / M chars)", name: "Character", fieldKeys: ["inputCostPerMillionCharacters"], requireAll: true },
    {
      id: "token",
      label: "Token ($/M audio out + $/M text in)",
      name: "Token",
      fieldKeys: ["outputCostPerMillionAudioTokens", "inputCostPerMillionTextTokens"],
      requireAll: true,
    },
    { id: "utf8_byte", label: "UTF-8 byte ($ / M bytes)", name: "UTF-8 byte", fieldKeys: ["inputCostPerMillionUtf8Bytes"], requireAll: true },
  ],
  stt: [
    {
      id: "duration",
      label: "Duration ($ / minute — live and/or file)",
      name: "Duration",
      fieldKeys: ["inputCostPerMinuteLiveAudio", "inputCostPerMinuteUploadedFile"],
      requireAll: false,
    },
    {
      id: "token",
      label: "Token ($/M audio in + $/M text out)",
      name: "Token",
      fieldKeys: ["inputCostPerMillionAudioTokens", "outputCostPerMillionTextTokens"],
      requireAll: true,
    },
  ],
  // Single strategy today (Gemini Live, OpenAI Realtime are all token-billed);
  // duration-billed providers (ElevenLabs Agents) get a second entry here when
  // we support them — the enforcement piping is already in place.
  realtime: [
    {
      id: "token",
      label: "Token ($/M audio + $/M text, both directions)",
      name: "Token",
      fieldKeys: ["inputCostPerMillionAudioTokens", "outputCostPerMillionAudioTokens", "inputCostPerMillionTextTokens", "outputCostPerMillionTextTokens"],
      optionalFieldKeys: ["cacheReadCostPerMillionAudioTokens", "cacheReadCostPerMillionTextTokens"],
      requireAll: true,
    },
  ],
};

/** Derive an existing row's strategy from which rates it carries; new rows
 *  default to the kind's most common strategy (first in the list). */
const strategyOf = (kind: Kind, row: Record<string, unknown> | null): string => {
  const defs = PRICING_STRATEGIES[kind];
  if (!defs) return "";
  if (row) {
    const match = defs.find((s) => s.fieldKeys.some((k) => row[k] != null));
    if (match) return match.id;
  }
  return defs[0].id;
};

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

/** Full-precision decimal rendering for tiny per-unit rates — never
 *  scientific notation (5e-7 is unreadable next to $0.0000005). */
const formatRate = (v: number | null | undefined): string => {
  if (v === null || v === undefined) return "—";
  if (Number.isInteger(v)) return `$${v}`;
  const fixed = v.toFixed(12).replace(/0+$/, "").replace(/\.$/, "");
  return `$${fixed}`;
};

/** Plain-decimal string for form inputs — String(7.5e-7) renders scientific
 *  notation, which is not editable pricing. Storage is exact decimal(18,12);
 *  this only affects how the JS number round-trips into the input. */
const rateInputValue = (v: unknown): string => {
  if (v === null || v === undefined) return "";
  const n = v as number;
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(12).replace(/0+$/, "").replace(/\.$/, "");
};

const sourceTone = (source: string): string => {
  if (source.startsWith("admin")) return "text-purple-600 dark:text-purple-400";
  if (source === "seed") return "text-gray-500 dark:text-gray-400";
  return "text-blue-600 dark:text-blue-400"; // litellm / vendor URLs
};

/** Vendor-URL sources (the LiteLLM sync stores the provider's pricing page
 *  URL) render as their hostname; the full value lives in the tooltip. */
const sourceLabel = (source: string): string => {
  if (!source.startsWith("http")) return source;
  try {
    return new URL(source).hostname.replace(/^www\./, "");
  } catch {
    return source;
  }
};

/** Block-level truncating cell text — Text alone renders inline, so the
 *  truncate class can't constrain it and long values overlap neighboring
 *  grid columns. */
const CellText = ({ children }: { children: string }) => (
  <div
    title={children}
    className="min-w-0 overflow-hidden"
  >
    <Text className="block truncate text-sm">{children}</Text>
  </div>
);

const SourceCell = ({ source }: { source: string }) => (
  <div
    title={source}
    className="min-w-0 overflow-hidden"
  >
    <Text className={`block truncate text-xs font-medium ${sourceTone(source)}`}>{sourceLabel(source)}</Text>
  </div>
);

const ValidityCell = ({ row }: { row: BaseRow }) => (
  <View className="flex flex-col">
    <Text className="text-xs text-gray-700 dark:text-gray-300">{formatDateShort(row.validFrom)}</Text>
    {row.validTo && <Text className="text-[11px] text-amber-600 dark:text-amber-500">expired {formatDateShort(row.validTo)}</Text>}
  </View>
);

/** One right-aligned table column per rate unit. */
const rateColumn = <T extends BaseRow>(f: RateField, width = "110px"): AdminColumn<T> => ({
  key: f.key,
  header: `$/${f.short}`,
  width,
  align: "right",
  render: (r) => <Text className="text-sm tabular-nums">{formatRate((r as unknown as Record<string, unknown>)[f.key] as number | null)}</Text>,
  sortValue: (r) => ((r as unknown as Record<string, unknown>)[f.key] as number | null) ?? null,
});

// ---------------------------------------------------------------------------
// Supersede dialog — one component, parameterized by kind. POST expires the
// active (provider, model) row server-side and inserts a fresh snapshot;
// rows are never edited in place, so "edit" here means "supersede".
// ---------------------------------------------------------------------------

type DialogState = { isOpen: false } | { isOpen: true; row: Record<string, unknown> | null };

const RateDialog = ({
  kind,
  state,
  providers,
  onClose,
  onSaved,
}: {
  kind: Kind;
  state: DialogState;
  providers: string[];
  onClose: () => void;
  onSaved: () => void;
}) => {
  const row = state.isOpen ? state.row : null;
  const fields = RATE_FIELDS[kind];

  const [modelName, setModelName] = useState("");
  const [provider, setProvider] = useState("");
  const [notes, setNotes] = useState("");
  const [rates, setRates] = useState<Record<string, string>>({});
  const [strategy, setStrategy] = useState<string>("");
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Strategy kinds (tts/stt/realtime) show only the picked strategy's fields;
  // values typed under another strategy stay in state but are neither shown
  // nor submitted.
  const strategyDefs = PRICING_STRATEGIES[kind];
  const activeStrategy = strategyDefs?.find((s) => s.id === strategy);
  const activeFields = useMemo(() => {
    if (!activeStrategy) return fields;
    return fields.filter((f) => activeStrategy.fieldKeys.includes(f.key) || activeStrategy.optionalFieldKeys?.includes(f.key));
  }, [fields, activeStrategy]);

  // Seed the form whenever the dialog opens (fresh for "add", prefilled for
  // "supersede from row").
  useEffect(() => {
    if (!state.isOpen) return;
    setFormError(null);
    setModelName((row?.modelName as string) ?? "");
    setProvider(((row?.provider ?? row?.apiProvider) as string) ?? (kind === "completions" ? (providers[0] ?? "") : ""));
    setNotes((row?.notes as string) ?? "");
    setStrategy(strategyOf(kind, row));
    const seeded: Record<string, string> = {};
    for (const f of fields) {
      seeded[f.key] = rateInputValue(row?.[f.key]);
    }
    setRates(seeded);
  }, [state.isOpen]);

  const save = async () => {
    setFormError(null);

    if (!modelName.trim() || !provider.trim()) {
      setFormError("Model name and provider are required.");
      return;
    }

    const body: Record<string, unknown> = { modelName: modelName.trim() };
    if (kind === "completions") {
      body.apiProvider = provider;
    } else {
      body.provider = provider.trim();
    }
    if (notes.trim()) body.notes = notes.trim();

    let populatedCount = 0;
    for (const f of activeFields) {
      const raw = (rates[f.key] ?? "").trim();
      if (raw === "") continue;
      const parsed = parseFloat(raw);
      if (Number.isNaN(parsed) || parsed < 0) {
        setFormError(`${f.label} must be a non-negative number.`);
        return;
      }
      body[f.key] = parsed;
      populatedCount++;
    }

    if (kind === "completions") {
      if (body.inputCostPerMillionTokens === undefined || body.outputCostPerMillionTokens === undefined) {
        setFormError("Input and output rates are required for completions models.");
        return;
      }
    } else if (activeStrategy) {
      if (activeStrategy.requireAll) {
        // Only the strategy's core meters are required; optionalFieldKeys
        // (cache-read discounts) may stay blank.
        const missing = activeFields.find((f) => activeStrategy.fieldKeys.includes(f.key) && body[f.key] === undefined);
        if (missing) {
          setFormError(`${missing.label} is required for this pricing strategy.`);
          return;
        }
      } else if (populatedCount === 0) {
        setFormError("At least one rate of the selected strategy is required.");
        return;
      }
    } else if (populatedCount === 0) {
      setFormError("At least one rate is required — a row with no rates resolves as unmappable.");
      return;
    }

    setIsSaving(true);
    try {
      await makeRequest(ENDPOINTS[kind], { method: "POST", body });
      onSaved();
      onClose();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : "Failed to save rate");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      isOpen={state.isOpen}
      onClose={onClose}
      title={row ? `${row.validTo ? "Restore" : "Supersede"} rate — ${row.provider ?? row.apiProvider}/${row.modelName}` : "Add rate"}
      size={kind === "completions" ? "lg" : "xl"}
      onSave={save}
      saveLabel={row ? (row.validTo ? "Restore rate" : "Supersede rate") : "Create rate"}
      isSaving={isSaving}
    >
      <View className="max-h-[70vh] overflow-y-auto p-4 flex flex-col gap-4">
        <Text className="text-xs text-gray-500 dark:text-gray-400">
          {row?.validTo
            ? "This model is retired (no active rate — billing alerts it as unmappable). Saving inserts a fresh active snapshot from the values below."
            : "Saving expires the current active row for this provider/model and inserts a new snapshot — history is kept, and sessions billed under the old rate keep resolving against it."}
        </Text>

        <View className="grid grid-cols-2 gap-3">
          <InputField
            label="Model name"
            value={modelName}
            onChange={setModelName}
            placeholder="e.g. gpt-4o"
          />
          {kind === "completions" ? (
            <DropdownField
              label="Provider"
              value={provider}
              onChange={(v: string) => setProvider(v)}
              options={providers.map((p) => ({ label: p, value: p }))}
            />
          ) : (
            <InputField
              label="Provider"
              value={provider}
              onChange={setProvider}
              placeholder="e.g. cartesia"
            />
          )}
        </View>

        {strategyDefs && (
          <DropdownField
            label="Pricing strategy"
            value={strategy}
            onChange={(v: string) => setStrategy(v)}
            options={strategyDefs.map((s) => ({ label: s.label, value: s.id }))}
          />
        )}

        <View className="grid grid-cols-2 gap-3">
          {activeFields.map((f) => (
            <View
              key={f.key}
              className="flex flex-col gap-1"
            >
              <InputField
                label={f.label}
                value={rates[f.key] ?? ""}
                onChange={(v: string) => setRates((prev) => ({ ...prev, [f.key]: v }))}
                placeholder={
                  activeStrategy?.requireAll && activeStrategy.fieldKeys.includes(f.key)
                    ? "required for this strategy"
                    : "leave blank if not billed in this unit"
                }
              />
              {f.hint && <Text className="text-[11px] text-gray-400 dark:text-gray-500">{f.hint}</Text>}
            </View>
          ))}
        </View>

        <InputField
          label="Notes"
          type="textarea"
          value={notes}
          onChange={setNotes}
          placeholder="e.g. verified https://developers.openai.com/api/docs/pricing 2026-08-10"
        />

        {formError && (
          <Alert variant="destructive">
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        )}
      </View>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Detail drawer — slides in from the right on row click. Owns everything
// that isn't the edit form: current rate breakdown, full snapshot history,
// and the expire action. The dialog stays a pure form on top of it.
// ---------------------------------------------------------------------------

const KIND_LABELS: Record<Kind, string> = {
  completions: "Completions",
  tts: "Text to Speech",
  stt: "Speech to Text",
  realtime: "Realtime",
};

const ModelDetailDrawer = ({
  kind,
  row,
  providers,
  onClose,
  onChanged,
}: {
  kind: Kind;
  row: BaseRow | null;
  providers: string[];
  onClose: () => void;
  onChanged: () => void;
}) => {
  const fields = RATE_FIELDS[kind];

  const historyUrl = row
    ? `${ENDPOINTS[kind]}?includeHistory=true&model=${encodeURIComponent(row.modelName)}&provider=${encodeURIComponent(providerOf(row))}`
    : null;
  const { data, isLoading, refresh } = useAdminFetch<{ data: BaseRow[] }>(historyUrl);
  // API returns validFrom DESC within a model; the active row (if any) is
  // the model's truth, else its latest snapshot (retired model).
  const history = useMemo(() => data?.data ?? [], [data]);
  const current = history.find((h) => !h.validTo) ?? history[0] ?? row;
  const isRetired = !!current?.validTo;

  const [dialog, setDialog] = useState<DialogState>({ isOpen: false });
  const [armExpire, setArmExpire] = useState(false);
  const [isExpiring, setIsExpiring] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    setArmExpire(false);
    setActionError(null);
  }, [row]);

  const expire = async () => {
    if (!current) return;
    setIsExpiring(true);
    setActionError(null);
    try {
      await makeRequest(`${ENDPOINTS[kind]}/${current.id}`, { method: "DELETE" });
      setArmExpire(false);
      refresh();
      onChanged();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Failed to expire rate");
    } finally {
      setIsExpiring(false);
    }
  };

  if (!row) return null;

  const currentRecord = current as unknown as Record<string, unknown>;
  const populated = fields.filter((f) => currentRecord[f.key] !== null && currentRecord[f.key] !== undefined);
  const notes = currentRecord.notes as string | undefined;

  return (
    <>
      <AdminDetailDrawer
        isOpen={!!row}
        onClose={onClose}
        title={row.modelName}
        subtitle={`${providerOf(row)} · ${KIND_LABELS[kind]}`}
        width={480}
        // Suspended while the supersede dialog is open — clicks inside the
        // dialog land outside the drawer panel and must not close it.
        closeOnOutsideClick={!dialog.isOpen}
      >
        {isRetired && (
          <Alert variant="warning">
            <AlertDescription>This model is retired — no active rate. Billing alerts it as unmappable until a rate is restored.</AlertDescription>
          </Alert>
        )}

        <Section title={isRetired ? "Last rate (retired)" : "Current rate"}>
          <View className="flex flex-col gap-1.5">
            {populated.length === 0 && <Text className="text-sm text-red-500 dark:text-red-400">No rates on this snapshot — resolves unmappable.</Text>}
            {PRICING_STRATEGIES[kind] && populated.length > 0 && (
              <Field label="Strategy">
                <Text className="text-sm font-medium text-purple-600 dark:text-purple-400">
                  {PRICING_STRATEGIES[kind]!.find((s) => s.id === strategyOf(kind, currentRecord))?.name}
                </Text>
              </Field>
            )}
            {fields.map((f) => (
              <Field
                key={f.key}
                label={f.label}
              >
                <Text
                  className={`text-sm tabular-nums ${currentRecord[f.key] === null || currentRecord[f.key] === undefined ? "text-gray-400 dark:text-gray-600" : ""}`}
                >
                  {formatRate(currentRecord[f.key] as number | null)}
                </Text>
              </Field>
            ))}
            <Field label="Source">
              <SourceCell source={current.source} />
            </Field>
            <Field label="Effective">
              <Text className="text-sm tabular-nums">
                {formatDateShort(current.validFrom)} → {current.validTo ? formatDateShort(current.validTo) : "now"}
              </Text>
            </Field>
            {notes && (
              <Field label="Notes">
                <Text className="whitespace-pre-wrap break-words text-sm">{notes}</Text>
              </Field>
            )}
          </View>
        </Section>

        <Section title="Rate history">
          {isLoading ? (
            <Text className="text-xs text-gray-400 dark:text-gray-500">Loading…</Text>
          ) : (
            <View className="flex flex-col gap-2">
              {history.map((h) => {
                const record = h as unknown as Record<string, unknown>;
                return (
                  <View
                    key={h.id}
                    className="flex flex-col gap-1 rounded-lg border border-gray-100 dark:border-gray-800 p-3"
                  >
                    <View className="flex flex-row items-center justify-between gap-3">
                      <Text
                        className={`text-xs tabular-nums ${h.validTo ? "text-gray-500 dark:text-gray-400" : "font-medium text-green-600 dark:text-green-400"}`}
                      >
                        {formatDateShort(h.validFrom)} → {h.validTo ? formatDateShort(h.validTo) : "now"}
                      </Text>
                      <View className="flex min-w-0 flex-row items-center justify-end gap-2">
                        {PRICING_STRATEGIES[kind] && (
                          <Text className="shrink-0 text-[11px] font-medium text-purple-600 dark:text-purple-400">
                            {PRICING_STRATEGIES[kind]!.find((s) => s.id === strategyOf(kind, record))?.name}
                          </Text>
                        )}
                        <View className="w-32 shrink-0 text-right">
                          <SourceCell source={h.source} />
                        </View>
                      </View>
                    </View>
                    {fields.map((f) => (
                      <View
                        key={f.key}
                        className="flex flex-row items-baseline justify-between gap-3"
                      >
                        <Text className="text-xs text-gray-400 dark:text-gray-500">{f.label}</Text>
                        <Text
                          className={`text-xs tabular-nums ${
                            record[f.key] === null || record[f.key] === undefined ? "text-gray-400 dark:text-gray-600" : "text-gray-700 dark:text-gray-200"
                          }`}
                        >
                          {formatRate(record[f.key] as number | null)}
                        </Text>
                      </View>
                    ))}
                    {!!record.notes && (
                      <Text className="whitespace-pre-wrap break-words text-xs text-gray-500 dark:text-gray-400">{record.notes as string}</Text>
                    )}
                  </View>
                );
              })}
            </View>
          )}
        </Section>

        {actionError && (
          <Alert variant="destructive">
            <AlertDescription>{actionError}</AlertDescription>
          </Alert>
        )}

        <View className="flex flex-col gap-3 border-t border-gray-100 dark:border-gray-800 pt-4">
          <View className="flex flex-row items-center justify-between gap-3">
            <Text className="min-w-0 text-xs text-gray-500 dark:text-gray-400">
              {isRetired ? "Insert a fresh active snapshot from the last rate." : "Expire this rate and insert a new snapshot in its place."}
            </Text>
            <Button
              variant="success"
              size="sm"
              outline
              onClick={() => setDialog({ isOpen: true, row: currentRecord })}
              className="shrink-0"
            >
              <Icon
                name={isRetired ? "add" : "edit"}
                size={14}
              />
              {isRetired ? "Restore rate" : "Supersede rate"}
            </Button>
          </View>

          {!isRetired && (
            <View className="flex flex-row items-center justify-between gap-3">
              <Text className="min-w-0 text-xs text-gray-500 dark:text-gray-400">
                {armExpire
                  ? "This leaves the model unpriced — billing will alert unmappable until a rate is restored."
                  : "Retire this rate without a replacement."}
              </Text>
              <Button
                variant="danger"
                size="sm"
                outline
                onClick={() => (armExpire ? expire() : setArmExpire(true))}
                disabled={isExpiring}
              >
                <Icon
                  name={isExpiring ? "loader" : "close"}
                  size={14}
                  className={isExpiring ? "animate-spin" : undefined}
                />
                {armExpire ? "Confirm expire" : "Expire rate"}
              </Button>
            </View>
          )}
        </View>
      </AdminDetailDrawer>

      <RateDialog
        kind={kind}
        state={dialog}
        providers={providers}
        onClose={() => setDialog({ isOpen: false })}
        onSaved={() => {
          refresh();
          onChanged();
        }}
      />
    </>
  );
};

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

const PAGE_SIZE = 50;

/** Column keys whose API sort key differs from the column key itself. The
 *  speech tables' rate columns are keyed by their field names already; these
 *  are the completions shorthands plus the shared Effective column. */
const SORT_PARAM_KEYS: Record<string, string> = {
  input: "inputCostPerMillionTokens",
  output: "outputCostPerMillionTokens",
  cacheRead: "cacheReadCostPerMillionTokens",
  cacheWrite: "cacheWriteCostPerMillionTokens",
  validity: "validFrom",
};

type SortState = { key: string; dir: "asc" | "desc" };

const useDefinitions = <T,>(kind: Kind, opts: { status: StatusFilter; provider: string; search: string; page: number; sort: SortState }) => {
  // Filtering/search/pagination are all server-side: the pricing tables are
  // snapshot histories (dev's completions table holds ~59k rows), so any
  // client-side collapse over a capped fetch silently truncates. The server
  // collapses to one row per model — the active snapshot, else the latest
  // expired one (a retired model, clickable to restore) — and paginates over
  // MODELS. Per-model raw history still lives in the detail drawer's
  // model-scoped fetch.
  const params = new URLSearchParams();
  if (opts.status !== "active") params.set("status", opts.status);
  if (opts.provider) params.set("provider", opts.provider);
  if (opts.search) params.set("search", opts.search);
  params.set("page", String(opts.page));
  params.set("pageSize", String(PAGE_SIZE));
  // Sort is server-side like everything else on this page: rows are one
  // 50-model page of the collapsed listing, so a client-side sort would only
  // reorder the visible page while masquerading as a global sort (an older
  // API build ignores these params and falls back to provider/model order).
  params.set("sort", SORT_PARAM_KEYS[opts.sort.key] ?? opts.sort.key);
  params.set("sortDir", opts.sort.dir);
  return useAdminFetch<{ data: T[]; total: number; providers: string[] }>(`${ENDPOINTS[kind]}?${params.toString()}`);
};

/** Provider column differs by table (provider vs apiProvider). */
const providerOf = (row: BaseRow): string =>
  (((row as unknown as Record<string, unknown>).provider ?? (row as unknown as Record<string, unknown>).apiProvider) as string) ?? "";

type StatusFilter = "active" | "retired" | "all";

const PricingTab = <T extends BaseRow>({ kind, columns }: { kind: Kind; columns: AdminColumn<T>[] }) => {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("active");
  const [addDialog, setAddDialog] = useState<DialogState>({ isOpen: false });
  const [selected, setSelected] = useState<T | null>(null);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [providerFilter, setProviderFilter] = useState("");
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortState>({ key: "provider", dir: "asc" });

  // Server-side search — debounce so typing isn't a query per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchText.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  // Any filter change restarts from page 1.
  useEffect(() => {
    setPage(1);
  }, [statusFilter, providerFilter, debouncedSearch, sort]);

  const { data, isLoading, error, refresh } = useDefinitions<T>(kind, {
    status: statusFilter,
    provider: providerFilter,
    search: debouncedSearch,
    page,
    sort,
  });
  const rows = useMemo(() => data?.data ?? [], [data]);
  const total = data?.total ?? 0;

  // Server-provided facet: the ApiProvider enum for completions, distinct
  // observed providers for the free-form speech/realtime tables. Union in
  // the active selection so it stays visible/clearable even mid-refetch.
  const providers = useMemo(() => Array.from(new Set([...(data?.providers ?? []), providerFilter].filter(Boolean))).sort(), [data, providerFilter]);
  const providerOptions = useMemo(() => [{ label: "All providers", value: "" }, ...providers.map((p) => ({ label: p, value: p }))], [providers]);

  return (
    <View className="mt-6 flex flex-col gap-3">
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <View className="flex flex-row items-center justify-between gap-3">
        <View className="flex w-full flex-row items-center gap-3">
          <View className="w-full max-w-sm">
            <InputField
              placeholder="Search models…"
              value={searchText}
              onChange={setSearchText}
            />
          </View>
          <View className="w-48 shrink-0">
            <DropdownField
              options={providerOptions}
              value={providerFilter}
              onChange={(v: string) => setProviderFilter(v)}
            />
          </View>
          <View className="w-36 shrink-0">
            <DropdownField
              options={[
                { label: "Active", value: "active" },
                { label: "Retired", value: "retired" },
                { label: "All", value: "all" },
              ]}
              value={statusFilter}
              onChange={(v: string) => setStatusFilter(v as StatusFilter)}
            />
          </View>
        </View>
        <View className="flex flex-row items-center gap-4 shrink-0">
          <Button
            variant="success"
            size="sm"
            outline
            onClick={() => setAddDialog({ isOpen: true, row: null })}
          >
            <Icon
              name="add"
              size={14}
            />
            Add rate
          </Button>
        </View>
      </View>
      <AdminTable<T>
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        onRowClick={(row) => setSelected(row)}
        selectedKey={selected?.id ?? null}
        isLoading={isLoading}
        emptyState={{ icon: "search", title: "No pricing rows", description: "Add a rate to price this model kind." }}
        serverSort={{ sort, onChange: setSort }}
        pagination={{ page, pageSize: PAGE_SIZE, total, onPageChange: setPage }}
      />
      <ModelDetailDrawer
        kind={kind}
        row={selected}
        providers={providers}
        onClose={() => setSelected(null)}
        onChanged={refresh}
      />
      <RateDialog
        kind={kind}
        state={addDialog}
        providers={providers}
        onClose={() => setAddDialog({ isOpen: false })}
        onSaved={refresh}
      />
    </View>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const AdminModelPricingPage = () => {
  // Active tab pinned in the URL (?tab=tts) so views are linkable and
  // survive refresh; the default tab keeps a clean URL.
  const [searchParams, setSearchParams] = useSearchParams();
  const KINDS: Kind[] = ["completions", "tts", "stt", "realtime"];
  const tabParam = searchParams.get("tab") as Kind | null;
  const activeTab: Kind = tabParam && KINDS.includes(tabParam) ? tabParam : "completions";
  const setActiveTab = (id: Kind) => {
    const next = new URLSearchParams(searchParams);
    if (id === "completions") next.delete("tab");
    else next.set("tab", id);
    setSearchParams(next, { replace: true });
  };

  const completionsColumns = useMemo<AdminColumn<CompletionsRow>[]>(
    () => [
      { key: "provider", header: "Provider", width: "120px", render: (r) => <CellText>{r.apiProvider}</CellText>, sortValue: (r) => r.apiProvider },
      { key: "model", header: "Model", width: "1.5fr", render: (r) => <CellText>{r.modelName}</CellText>, sortValue: (r) => r.modelName },
      {
        key: "input",
        header: "Input $/M",
        width: "110px",
        align: "right",
        render: (r) => <Text className="text-sm tabular-nums">{formatRate(r.inputCostPerMillionTokens)}</Text>,
        sortValue: (r) => r.inputCostPerMillionTokens,
      },
      {
        key: "output",
        header: "Output $/M",
        width: "110px",
        align: "right",
        render: (r) => <Text className="text-sm tabular-nums">{formatRate(r.outputCostPerMillionTokens)}</Text>,
        sortValue: (r) => r.outputCostPerMillionTokens,
      },
      {
        key: "cacheRead",
        header: "Cache read $/M",
        width: "130px",
        align: "right",
        render: (r) => <Text className="text-sm tabular-nums">{formatRate(r.cacheReadCostPerMillionTokens)}</Text>,
        sortValue: (r) => r.cacheReadCostPerMillionTokens,
      },
      {
        key: "cacheWrite",
        header: "Cache write $/M",
        width: "130px",
        align: "right",
        render: (r) => <Text className="text-sm tabular-nums">{formatRate(r.cacheWriteCostPerMillionTokens)}</Text>,
        sortValue: (r) => r.cacheWriteCostPerMillionTokens,
      },
      { key: "source", header: "Source", width: "160px", render: (r) => <SourceCell source={r.source} />, sortValue: (r) => r.source },
      { key: "validity", header: "Effective", width: "140px", render: (r) => <ValidityCell row={r} />, sortValue: (r) => r.validFrom },
    ],
    [],
  );

  const ttsColumns = useMemo<AdminColumn<TtsRow>[]>(
    () => [
      { key: "provider", header: "Provider", width: "120px", render: (r) => <CellText>{r.provider}</CellText>, sortValue: (r) => r.provider },
      { key: "model", header: "Model", width: "1.2fr", render: (r) => <CellText>{r.modelName}</CellText>, sortValue: (r) => r.modelName },
      ...TTS_RATE_FIELDS.map((f) => rateColumn<TtsRow>(f)),
      { key: "source", header: "Source", width: "160px", render: (r) => <SourceCell source={r.source} />, sortValue: (r) => r.source },
      { key: "validity", header: "Effective", width: "140px", render: (r) => <ValidityCell row={r} />, sortValue: (r) => r.validFrom },
    ],
    [],
  );

  const sttColumns = useMemo<AdminColumn<SttRow>[]>(
    () => [
      { key: "provider", header: "Provider", width: "120px", render: (r) => <CellText>{r.provider}</CellText>, sortValue: (r) => r.provider },
      { key: "model", header: "Model", width: "1.2fr", render: (r) => <CellText>{r.modelName}</CellText>, sortValue: (r) => r.modelName },
      ...STT_RATE_FIELDS.map((f) => rateColumn<SttRow>(f)),
      { key: "source", header: "Source", width: "160px", render: (r) => <SourceCell source={r.source} />, sortValue: (r) => r.source },
      { key: "validity", header: "Effective", width: "140px", render: (r) => <ValidityCell row={r} />, sortValue: (r) => r.validFrom },
    ],
    [],
  );

  const realtimeColumns = useMemo<AdminColumn<RealtimeRow>[]>(
    () => [
      { key: "provider", header: "Provider", width: "120px", render: (r) => <CellText>{r.provider}</CellText>, sortValue: (r) => r.provider },
      { key: "model", header: "Model", width: "220px", render: (r) => <CellText>{r.modelName}</CellText>, sortValue: (r) => r.modelName },
      ...REALTIME_RATE_FIELDS.map((f) => rateColumn<RealtimeRow>(f, "110px")),
      { key: "source", header: "Source", width: "160px", render: (r) => <SourceCell source={r.source} />, sortValue: (r) => r.source },
      { key: "validity", header: "Effective", width: "140px", render: (r) => <ValidityCell row={r} />, sortValue: (r) => r.validFrom },
    ],
    [],
  );

  return (
    <PageLayout
      title="Model Pricing"
      description="Provider cost basis for completions, speech, and realtime billing. Rates are snapshot rows — saving supersedes the active row, never edits it, so historical sessions keep their prices."
      tabs={[
        { id: "completions", label: "Completions" },
        { id: "tts", label: "Text to Speech" },
        { id: "stt", label: "Speech to Text" },
        { id: "realtime", label: "Realtime" },
      ]}
      activeTab={activeTab}
      onTabChange={(id) => setActiveTab(id as Kind)}
    >
      {activeTab === "completions" && (
        <PricingTab<CompletionsRow>
          kind="completions"
          columns={completionsColumns}
        />
      )}
      {activeTab === "tts" && (
        <PricingTab<TtsRow>
          kind="tts"
          columns={ttsColumns}
        />
      )}
      {activeTab === "stt" && (
        <PricingTab<SttRow>
          kind="stt"
          columns={sttColumns}
        />
      )}
      {activeTab === "realtime" && (
        <PricingTab<RealtimeRow>
          kind="realtime"
          columns={realtimeColumns}
        />
      )}
    </PageLayout>
  );
};
